import { useEffect, useMemo, useState } from "react";
import {
  getRoster,
  sortPractitionersDdsThenHyg,
  getUiTypeFromPractitioner,
  isFinancialModuleType,
} from "@utils/practitionerUtils";

/**
 * Shared practitioner selection + quick look state for report pages (FinancialReport, MonthlyReconReport).
 * @param {object} practitionersData - API response (e.g. { results: [] } or array)
 * @param {{ withTypeFilter?: boolean }} options - If true, exposes typeFilter, setTypeFilter and syncs activePid to filteredRoster (DDS/HYG/ALL)
 * @returns roster, rosterSorted, filteredRoster (when withTypeFilter), typeFilter/setTypeFilter (when withTypeFilter), activePid, setActivePid, selectedPractitioner, quick look state
 */
export function usePractitionerSelection(practitionersData, options = {}) {
  const { withTypeFilter = false } = options;

  const roster = useMemo(() => {
    const items = getRoster(practitionersData);
    const list = Array.isArray(items) ? items : [];
    // Temporary hold: financial report pages (this hook's only consumers) show only
    // Dentists + Hygienists. See filterToFinancialTypes in practitionerUtils.js.
    return list.filter((p) => p.status !== "inactive" && isFinancialModuleType(p));
  }, [practitionersData]);

  const rosterSorted = useMemo(() => sortPractitionersDdsThenHyg(roster), [roster]);

  const [typeFilter, setTypeFilter] = useState("ALL");
  const filteredRoster = useMemo(() => {
    if (!withTypeFilter) return rosterSorted;
    if (typeFilter === "DDS" || typeFilter === "HYG") {
      const list = roster.filter((p) => getUiTypeFromPractitioner(p) === typeFilter);
      return sortPractitionersDdsThenHyg(list);
    }
    return rosterSorted;
  }, [roster, rosterSorted, typeFilter, withTypeFilter]);

  const listToSync = withTypeFilter ? filteredRoster : rosterSorted;

  const [activePid, setActivePid] = useState("");
  const [quickLookOpen, setQuickLookOpen] = useState(false);
  const [activePractitioner, setActivePractitioner] = useState(null);

  useEffect(() => {
    if (listToSync.length === 0) {
      if (activePid !== "") setActivePid("");
      return;
    }
    if (activePid === "") {
      setActivePid(listToSync[0]?.id ?? "");
      return;
    }
    const found = listToSync.some((p) => String(p.id) === String(activePid));
    if (!found) setActivePid(listToSync[0]?.id ?? "");
  }, [listToSync, activePid]);

  const selectedPractitioner = useMemo(() => {
    const p = roster.find((pract) => String(pract.id) === String(activePid));
    return p ?? roster[0] ?? null;
  }, [roster, activePid]);

  function openQuickLook(p) {
    setActivePractitioner(p);
    setQuickLookOpen(true);
  }

  const out = {
    roster,
    rosterSorted,
    activePid,
    setActivePid,
    selectedPractitioner,
    quickLookOpen,
    activePractitioner,
    openQuickLook,
    setQuickLookOpen,
    setActivePractitioner,
  };
  if (withTypeFilter) {
    out.filteredRoster = filteredRoster;
    out.typeFilter = typeFilter;
    out.setTypeFilter = setTypeFilter;
  }
  return out;
}
